import React, { useEffect, useContext, useState } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import useFirstTime from "common/hooks/firstTime";
import useDebouncer from "common/hooks/debouncer";
import { TitleWithNav } from "common/TitleWithNav";
import { validateIPv4IPv6, safeStr } from "common/api";
import { showDynPolicyRate } from "views/Config/PolicyEdit/utils";
import HelpModal from "./Help";
import FilteringControls from "./FilteringControls";
import Actions from "./Actions";
import ListSubscriberGroups from "./Report/ListSubscriberGroups";
import { SubscriberGroupsChartsReport } from "./Report/SubscriberGroupsChartsReport";
import CreateSubscriberGroupModal from "./Report/CreateSubscriberGroupModal";
import UpdateShapingValueModal from "../SubscriberGroupsEdit/Shaping/Update";
import {
  doUpdateShaping,
  validate as validateShaping,
} from "../SubscriberGroupsEdit/Shaping";
import Statistics from "./Statistics/index";

const HeaderDiv = styled.div`
  display: flex;
  flex-direction: column;
  & .title {
    text-transform: uppercase;
  }
  & .context-menu {
    display: flex;
    flex-direction: row;
  }
`;

const WrapperTable = styled.div`
  margin: 0 20px 20px;
  visibility: ${(props) => props.show}
`;

const WrapperChart = styled.div`
  visibility: ${(props) => props.show}
`

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <a
        data-loading-effect="pulse"
        data-toggle="cardloading"
        title="Help"
        onClick={() => actions.send("open-help")}
      >
        <i className="material-icons">help_outline</i>
      </a>
      <a
        onClick={() => actions.send("do-load")}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Refresh"
      >
        <i className="material-icons">refresh</i>
      </a>
    </ul>
  );
};

const allSubscribersGroup = "all-subscribers";
const subsGroupsToPlotLimit = 50;
const tablePlaceholder = `SUBSCRIBER-GROUP         SUBS-ACTIVE  FL-ACTIVE FL-CREATED         MBYTES  CURR-Mbps  MEAN-Mbps    RTT-ms      RTX MAX-SPEED  CONGEST   LIFETIME     GROUP-TYPE`;
const BOXPLOT_METRICS_SET = new Set([
  "MEAN-Mbps",
  "SUBS-ACTIVE",
  "RTT-ms",
  "RTX",
  "CONGEST",
]);

const getSubscriberGroupTypeMap = (ifCl) => {
  return ifCl.run("show api subscriber-group").then((response) => {
    const [header, ...rows] = response.trim(/\s/).split("\n");
    const headerAttrs = header.split(/\s+/);
    const subscriberNameId = headerAttrs.findIndex(
      (el) => el === "SUBSCRIBER-GROUP"
    );
    const subscriberTypeId = headerAttrs.findIndex((el) => el === "GROUP-TYPE");
    return rows.reduce((acc, row) => {
      const rowAttrs = row.split(/\s+/);
      acc[rowAttrs[subscriberNameId]] = rowAttrs[subscriberTypeId];
      return acc;
    }, {});
  });
};

const getSubscriberThresholds = (ifCl) => {
  return ifCl.run("show subscriber metrics-thresholds").then((response) => {
    const rows = response.trim("\n").split("\n");
    return rows.reduce((acc, row) => {
      const [metric, value] = row.split(/:\s+/);
      acc[metric] = parseFloat(value);
      return acc;
    }, {});
  });
};

const getAllMetricsSubscriberGroups = (ifCl, direction) => {
  return ifCl.run(`show subscriber-group all metrics-${direction}`);
};

const parseSubsGroupsNames = (input) => {
  const lines = input.trim("\n").split("\n");
  let names = lines.map((line) => line.replace(/\s.*$/, ""));
  if (!names.includes(allSubscribersGroup)) {
    names.unshift(allSubscribersGroup);
  }
  return names;
};

const getFilteredTableData = (tableData, types) => {
  if (types.length === 0) {
    return tableData;
  }
  const typesSet = new Set([...types]);
  const [header, ...rows] = tableData.trim("\n").split("\n");
  const newRows = rows.reduce((acc, row) => {
    const rowAttrs = row.split(/\s+/);
    const type = rowAttrs[rowAttrs.length - 1];
    if (typesSet.has(type)) {
      acc.push(row);
    }
    return acc;
  }, []);
  return [header, ...newRows].join("\n");
};

const getSubsToPlot = (selected, types, map) => {
  if (types.length === 0 || selected === null) {
    return selected;
  }
  const typesSet = new Set([...types]);
  return selected.reduce((acc, value) => {
    const type = map[value];
    if (typesSet.has(type)) {
      acc.push(value);
    }
    return acc;
  }, []);
};

const openSubscriberGroupEdit = (subscriberGroup, settings, type) => {
  globalNavigate("viewStatusSubscriberGroups", {
    subscriberGroup,
    edit: true,
    type,
    ...settings
  });
};

function openRatePolicy(name, assignment, settings) {
  if (assignment === "configured") {
    globalNavigate("viewStatusSubscriberGroups", {
      policy: name,
      view: "policyRateEdit",
      ...settings
    });
  } else if (assignment === "assigned") {
    showDynPolicyRate(name, true);
  } else {
    offerDefineIfNotFound(name, settings);
  }
}

function offerDefineIfNotFound(name, settings) {
  confirmCreatePolicy()
    .then(() => {
      globalNavigate("viewPolicyRateEdit", {
        ...settings,
        create: name,
        returnView: "viewStatusSubscriberGroups",
      });
    })
    .catch(() => null);
}

function confirmCreatePolicy() {
  return new Promise((resolve, reject) =>
    showModalConfirm(
      `Confirm before continue`,
      `Warning: This policy is undefined, and has been assigned to some subscribers or subscriber-groups. Those subscribers are now getting a default policy from the configured rules, while thos subscriber-groups are getting no rate limitation. If you click "Define", you can now define this policy and then the assigned subscribers and groups will get it.`,
      "warning",
      () => resolve(),
      () => reject(),
      "DEFINE",
      "RETURN"
    )
  );
}

const applyShaping =
  (onSaveDone = doesNothing) =>
  ({ field, value, subscriberGroup }) =>
    doUpdateShaping({ subscriberGroup, direction: field, value }).then(
      onSaveDone
    );

const SubscriberGroups = ({
  returnView,
  ipAddress,
  direction,
  timePeriod,
  metric,
  use,
  types,
}) => {
  const actions = useContext(ActionsContext);
  const [groupTypes, setGroupTypes] = useState({});
  const [tableData, setTableData] = useState("");
  const [statisticsData, setStatisticsDate] = useState({});
  const [thresholds, setThresholds] = useState({});
  const [colorize, setColorize] = useState(false)
  const [settings, setSettings] = useState({
    direction: direction || "downlink",
    timePeriod: timePeriod || "oneDay",
    metric: metric || "averageSpeed",
    use: use || "ipAddress",
    types: types || [],
    ipAddress
  });
  const isFirstTime = useFirstTime();
  const debounce = useDebouncer(2100 /*ms*/);
  const [selected, setSelected] = useState(null);
  const [showCreateSubscriberGroupModal, setShowCreateSubscriberGroupModal] =
    useState(false);
  const [renderTable, setRenderTable] = useState(500);

  useEffect(() => () => actions.clear(), []);

  useEffect(
    () =>
      actions.recv("do-load", () => {
        doLoad();
      }
      ),
    []
  );

  useEffect(
    () =>
      actions.recv("create-group", () =>
        setShowCreateSubscriberGroupModal(true)
      ),
    []
  );

  useEffect(
    () =>
      actions.recv(
        "subscriber-group-policy-rate-clicked",
        ({ policyName, assignment }) => {
          openRatePolicy(policyName, assignment, settings);
        }
      ),
    [settings]
  );

  useEffect(
    () =>
      actions.recv(
        "subscriber-group-type-changed",
        ({ type, name }) => {
          setGroupTypes({...groupTypes, [name]: type})
        }
      ),
    []
  );

  useEffect(
    () =>
      actions.recv("subscriber-policy-rate-changed", () => {
        doLoad();
      }),
    []
  );

  useEffect(() => {
    if (Object.keys(groupTypes).length !== 0) {
      actions.recv(
        "subscriber-group-edit-clicked",
        ({ subscriberGroup }) => {
          const type = groupTypes[subscriberGroup];
          openSubscriberGroupEdit(subscriberGroup, settings, type);
        },
        true
      );
    }
  }, [settings, groupTypes]);

  useEffect(() => {
    doLoad();
  }, [settings.direction, settings.ipAddress, settings.timePeriod, JSON.stringify(groupTypes)]);

  const updateSelected = (newSelection) => {
    const assign = () => {
      if (newSelection.length > subsGroupsToPlotLimit) {
        showModalInfo(
          `Cannot select more than ${subsGroupsToPlotLimit} subscriber groups to plot. De-select some subscriber groups to select new ones.`
        );
        return;
      }
      setSelected(newSelection.map(({ name }) => name));
    };
    isFirstTime() ? assign() : debounce(assign);
  };

  const editSubscriberGroup = (subscriberGroup, type) => {
    globalNavigate("viewStatusSubscriberGroupsEdit", {
      returnView: "viewStatusSubscriberGroups",
      subscriberGroup,
      type
    });
  };

  const doLoad = () => {
    let promises = [getSubscriberGroupTypeMap(ifCl), getSubscriberThresholds(ifCl)];
    if (settings.ipAddress) {
      const { ipAddress } = settings;
      const subscriberGroupsCommand =
        ipAddress.length === 0 || validateIPv4IPv6(ipAddress) === true
          ? `show api subscriber ${ipAddress} subscriber-group`
          : `show api subscriber-id name ${safeStr(
              settings.ipAddress
            )} subscriber-group`;
      promises.push(
        ifCl
          .run(subscriberGroupsCommand)
          .then(parseSubsGroupsNames)
          .catch((e) => {
            return [allSubscribersGroup];
          })
      );
    }
    let subsGroups, subsTypes, thresholds;
    Promise.all(promises)
      .then(([responseTypes, responseThresholds, responseSubsGroups]) => {
        subsTypes = responseTypes;
        subsGroups = responseSubsGroups;
        thresholds = responseThresholds;
        return getAllMetricsSubscriberGroups(ifCl, settings.direction);
      })
      .then((responseAllMetrics) => {
        const [header, ...rows] = responseAllMetrics.split("\n");
        const headerWithType = [header, "GROUP-TYPE"].join("    ");
        const headerAttrs = headerWithType.trim("\n").split(/\s+/);
        const boxplotMetricsIndexMap = headerAttrs.reduce((acc, attr, index) => {
          if (BOXPLOT_METRICS_SET.has(attr)) {
            acc[attr] = index;
          }
          return acc;
        }, {});
        const boxplotIndexMetricsMap = headerAttrs.reduce((acc, attr, index) => {
          if (BOXPLOT_METRICS_SET.has(attr)) {
            acc[index] = attr;
          }
          return acc;
        }, {});

        if (!subsGroups || subsGroups.length === 0) {
          const parsedData = rows.reduce(
            (acc, row) => {
              const [nameGroup, ...attrs] = row.split(/\s+/);
              if (nameGroup) {
                const newRow = [nameGroup, ...attrs, subsTypes[nameGroup] ?? 'generic'].join("    ");
                acc.newRows.push(newRow);
                if (nameGroup !== "all-subscribers") {
                  acc.totalGroups += 1;
                  for (let i = 0; i < attrs.length; i++) {
                    if (boxplotIndexMetricsMap[i + 1]) {
                      if (acc[boxplotIndexMetricsMap[i + 1]]) {
                        acc[boxplotIndexMetricsMap[i + 1]].push(attrs[i]);
                      } else {
                        acc[boxplotIndexMetricsMap[i + 1]] = [attrs[i]];
                      }
                    }
                  }
                }
              }
              return acc;
            },
            { newRows: [], totalGroups:0 }
          );
          const tableData = [headerWithType, ...parsedData.newRows].join("\n");
          setGroupTypes(subsTypes);
          setTableData(tableData);
          setStatisticsDate(parsedData)
          setSettings({ ...settings, ["types"]: [] });
          setThresholds(thresholds);
          setRenderTable(false);
        } else {
          const subGroupsSet = new Set(subsGroups);
          const parsedData = rows.reduce((acc, row)=>{
            const [name, ...rest] = row.split(/\s+/);
            if (name && subGroupsSet.has(name)) {
              const newRow = [name, ...rest, subsTypes[name]].join("    ");
              acc.rows.push(newRow);
              acc.names.push(name);
              if (name !== "all-subscribers") {
                acc.totalGroups += 1;
                for (let i = 0; i < rest.length; i++) {
                  if (boxplotIndexMetricsMap[i + 1]) {
                    if (acc[boxplotIndexMetricsMap[i + 1]]) {
                      acc[boxplotIndexMetricsMap[i + 1]].push(rest[i]);
                    } else {
                      acc[boxplotIndexMetricsMap[i + 1]] = [rest[i]];
                    }
                  }
                }
              }
            }
            return acc;
          },{rows:[], names:[], totalGroups:0});
          const tableData = [headerWithType, ...parsedData.rows].join("\n");
          const namesSet = new Set(parsedData.names);
          const newSubTypes = Object.entries(subsTypes).reduce(
            (acc, [name, type]) => {
              if(namesSet.has(name)){
                acc[name] = type;
              }
              return acc;
            },
            {}
          );
          setStatisticsDate(parsedData)
          setGroupTypes(newSubTypes);
          setTableData(tableData);
          setRenderTable(false);
        }
      })
      .catch((error)=> {
        setTableData(tablePlaceholder);
        showModalError("Error", error);
      });
  };

  return (
    <div className="viewStatusSubscriberGroups col-lg-12 col-md-12 col-sm-12 col-xs-12">
      <div className="card">
        <HeaderDiv className="header block-header">
          <TitleWithNav returnView={returnView}>
            <h2 className="title">Subscriber Groups</h2>
          </TitleWithNav>
          <ContextMenu />
        </HeaderDiv>
        <div id="subsGroupsReportContainer" className="body flex-panel-column">
          <Actions />
          <FilteringControls
            settings={settings}
            onChange={(settings) => setSettings(settings)}
            groupTypes={groupTypes}
            thresholds={thresholds}
            setThresholds={setThresholds}
          />
          <UpdateShapingValueModal
            doValidate={validateShaping}
            doSave={applyShaping(doLoad)}
          />
          {renderTable ? (
            <Statistics
              data={statisticsData}
              thresholds={thresholds}
              setThresholds={setThresholds}
              colorize={colorize}
              setColorize={setColorize}
            />
          ) : null}
          <WrapperChart show={renderTable ? "visible" : "hidden"}>
            <SubscriberGroupsChartsReport
              direction={settings.direction}
              timePeriod={settings.timePeriod}
              metric={settings.metric}
              subsGroupsToPlot={getSubsToPlot(
                selected,
                settings.types,
                groupTypes
              )}
              ipAddress={settings.ipAddress}
              use={settings.use}
              groupTypes={groupTypes}
            />
          </WrapperChart>
          <WrapperTable show={renderTable ? "visible" : "hidden"}>
            <ListSubscriberGroups
              timePeriod={settings.timePeriod}
              types={settings.types}
              metric={settings.metric}
              tableData={getFilteredTableData(tableData, settings.types)}
              statisticsData={statisticsData}
              onPlotSelection={updateSelected}
              onRemoved={() => doLoad()}
              renderTable={renderTable}
              setRenderTable={setRenderTable}
              direction={settings.direction}
              colorize={colorize}
              thresholds={thresholds}
            />
          </WrapperTable>
          <CreateSubscriberGroupModal
            open={showCreateSubscriberGroupModal}
            onOk={(value) => editSubscriberGroup(value, "generic")}
            onClose={() => setShowCreateSubscriberGroupModal(false)}
          />
        </div>
        <HelpModal />
      </div>
    </div>
  );
};

export default SubscriberGroups;
